import { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../components/AdminHeader"; // твой общий хедер
import { api } from "../../api/client";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import { useNavigate } from "react-router-dom";
import "./index.css";

type StatusCountsResp = {
  ok: true;
  total: number;
  counts: Record<string, number>;
};

type CreatedByDayResp = {
  ok: true;
  byDay: Record<string, number>; // "YYYY-MM-DD": count
};

type ProblemUser = {
  id: string;
  name?: string;
  email?: string;
  createdAt?: number;
  status?: string;
  problemParfumes?: number;
};

type ProblemUsersResp = {
  ok: true;
  users: ProblemUser[];
};

export default function AdminHome() {
  const nav = useNavigate();

  // ------- state -------
  const [statusCounts, setStatusCounts] = useState<StatusCountsResp | null>(null);
  const [createdByDay, setCreatedByDay] = useState<CreatedByDayResp | null>(null);
  const [problemUsers, setProblemUsers] = useState<ProblemUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // create form
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [name, setName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  const canCreate = useMemo(
    () => Boolean(email && pass && name),
    [email, pass, name]
  );

  // ------- loaders -------
  async function loadAll() {
    try {
      setLoading(true);
      setErr("");

      const [sc, cd, pb] = await Promise.all([
        api<StatusCountsResp>("/admin/metrics/statusCounts", { method: "GET" }),
        api<CreatedByDayResp>("/admin/metrics/createdByDay", { method: "GET" }),
        api<ProblemUsersResp>("/admin/metrics/problemUsers", { method: "GET" }),
      ]);

      setStatusCounts(sc);
      setCreatedByDay(cd);
      setProblemUsers(pb.users || []);
    } catch (e: any) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  // ------- actions -------
  async function createAccount() {
    if (!canCreate) return;
    try {
      await api<{ ok: true; uid: string; role: "admin" | "client" }>(
        "/admin/create-account",
        {
          method: "POST",
          body: JSON.stringify({
            email,
            password: pass,
            name,
            role: isAdmin ? "admin" : "client",
          }),
        }
      );
      setEmail(""); setPass(""); setName(""); setIsAdmin(false);
      // обновим метрики (новый пользователь попадёт в счётчики/график)
      loadAll();
    } catch (e: any) {
      setErr(e.message || String(e));
    }
  }

  async function logout() {
    try {
      await signOut(auth);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      nav("/login", { replace: true });
    }
  }

  // ------- derived -------
  const counts = statusCounts?.counts || {};
  const total = statusCounts?.total || 0;

  // Приводим график к массиву точек текущего месяца по дням (чтобы были нули)
  const chartData = useMemo(() => {
    if (!createdByDay?.byDay) return [];
    // построим последовательность дат текущего месяца
    const start = new Date(); start.setDate(1); start.setHours(0,0,0,0);
    const end = new Date(start); end.setMonth(end.getMonth() + 1);

    const rows: { date: string; value: number }[] = [];
    for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().slice(0,10);
      rows.push({ date: key, value: createdByDay.byDay[key] || 0 });
    }
    return rows;
  }, [createdByDay]);

  return (
    <AdminLayout>
      <div className="dash">
        <div className="dash__top">
          <h1 className="dash__title">Дашборд</h1>
          <div className="dash__actions">
            <button className="btn btn--ghost" onClick={loadAll} disabled={loading}>
              {loading ? "Обновляем…" : "Обновить"}
            </button>
            <button className="btn" onClick={logout}>Выйти</button>
          </div>
        </div>

        {err && <div className="error">{err}</div>}

        {/* Статкарточки */}
        <section className="stats">
          <StatCard label="Всего пользователей" value={total} />
          <StatCard label="В процессе" value={counts["draft"] || 0} tone="muted" />
          <StatCard label="Формулы" value={(counts["formula_production"] || 0)} tone="blue" />
          <StatCard label="У клиента" value={(counts["in_client"] || 0)} tone="blue" />
          <StatCard label="Одобрено" value={counts["approved"] || 0} tone="green" />
          <StatCard label="Производство" value={counts["in_production"] || 0} tone="orange" />
          <StatCard label="Упаковка" value={counts["in_pack"] || 0} tone="amber" />
          <StatCard label="Готово к отправке" value={counts["ready_to_ship"] || 0} tone="cyan" />
          <StatCard label="Отправлено" value={counts["shipped"] || 0} tone="gold" />
          <StatCard label="Доставлено" value={counts["delivered"] || 0} tone="teal" />
        </section>

        {/* Мини-форма создания аккаунта */}
        <section className="createBox">
          <div className="createBox__head">
            <h2>Создать аккаунт</h2>
            <div className="seg">
              <button
                type="button"
                className={`seg__btn ${!isAdmin ? "seg__btn--active" : ""}`}
                onClick={() => setIsAdmin(false)}
              >
                Client
              </button>
              <button
                type="button"
                className={`seg__btn ${isAdmin ? "seg__btn--active" : ""}`}
                onClick={() => setIsAdmin(true)}
              >
                Admin
              </button>
            </div>
          </div>
          <div className="createBox__grid">
            <input
              className="inp"
              placeholder="Имя"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <input
              className="inp"
              placeholder="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            <input
              className="inp"
              placeholder="Пароль"
              type="text"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              autoComplete="new-password"
            />
            <button
              className="btn btn--primary"
              disabled={!canCreate}
              onClick={createAccount}
            >
              Создать
            </button>
          </div>
        </section>

        {/* График за месяц */}
        <section className="chart">
          <div className="chart__head">
            <h2>Новые аккаунты по дням (текущий месяц)</h2>
          </div>
          <MiniAreaChart data={chartData} height={140} />
        </section>

        {/* Проблемные клиенты */}
        <section className="problems">
          <div className="problems__head">
            <h2>Проблемные клиенты</h2>
            <span className="hint">Дали мало деталей в ассоциациях</span>
          </div>

          <div className="problems__list">
            {problemUsers.length === 0 && (
              <div className="muted">Отлично, проблемных нет</div>
            )}
            {problemUsers.map((u) => (
              <div className="pCard" key={u.id}>
                <div className="pCard__title">{u.name || "—"}</div>
                <div className="pCard__sub">{u.email || "—"}</div>
                <div className="pCard__meta">
                  <span>{u.problemParfumes || "-"} проблемных парфюмов</span>
                  
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}

/* ===== UI PARTS ===== */

function StatCard({ label, value, tone }: { label: string; value: number; tone?: "muted"|"blue"|"green"|"orange"|"amber"|"cyan"|"teal"|"gold" }) {
  return (
    <div className={`stat ${tone ? `stat--${tone}` : ""}`}>
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
    </div>
  );
}

// Лёгкий SVG-чарт (area)
function MiniAreaChart({ data, height = 140 }: { data: {date: string; value: number}[]; height?: number }) {
  const padding = 12;
  const w = Math.max(320, Math.min(920, data.length * 20));
  const h = height;

  const maxVal = Math.max(1, ...data.map(d => d.value));
  const scaleX = (i: number) => padding + (i * (w - 2*padding)) / Math.max(1, data.length - 1);
  const scaleY = (v: number) => h - padding - (v * (h - 2*padding)) / maxVal;

  const path = data.map((d, i) => `${i===0 ? "M" : "L"} ${scaleX(i)} ${scaleY(d.value)}`).join(" ");
  const area = `M ${padding} ${h - padding} ` +
               data.map((d, i) => `L ${scaleX(i)} ${scaleY(d.value)}`).join(" ") +
               ` L ${padding + (w - 2*padding)} ${h - padding} Z`;

  return (
    <div className="chart__scroller">
      <svg width={w} height={h} className="chart__svg" aria-label="Accounts by day">
        <defs>
          <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#111" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#111" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {/* ось X (низ) */}
        <line x1={padding} y1={h - padding} x2={w - padding} y2={h - padding} stroke="#eae6e0" />
        {/* площадь */}
        <path d={area} fill="url(#grad)" />
        {/* линия */}
        <path d={path} stroke="#111" fill="none" strokeWidth={2} />
      </svg>
    </div>
  );
}
